import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Users, X, ChevronUp } from "lucide-react";
import SailorSidebar from "./SailorSidebar";

/**
 * MobileRosterSheet — presents the athlete roster (SailorSidebar) as
 * a floating action button + bottom-sheet modal on viewports where
 * the side-column sidebar isn't visible (< lg).
 *
 * Without this, the tab body sits under a 500-row sidebar and the
 * coach has to scroll past the entire roster to reach the athlete
 * they just picked. The FAB collapses that into "one tap → pick →
 * back to reading".
 *
 * Behaviour:
 *   • Fixed bottom-right FAB shows the current athlete's name +
 *     roster count. Tapping opens the sheet.
 *   • Sheet slides up from the bottom, ~85 vh tall so the coach can
 *     still glance at the athlete card underneath if needed.
 *   • Picking an athlete auto-closes the sheet.
 *   • Only renders on < lg (Tailwind's lg = 1024px); on desktop the
 *     inline sidebar in App.js is used instead.
 */
export default function MobileRosterSheet({
  sailors,
  selectedId,
  onSelect,
  injuriesBySailor,
}) {
  const [open, setOpen] = useState(false);
  const selected = (sailors || []).find((s) => s.id === selectedId);

  // Lock body scroll while the sheet is open so the athlete card
  // underneath doesn't drift as the coach thumbs through the roster.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const activeCount = (sailors || []).filter((s) => !s.archived).length;

  return (
    <>
      {/* Floating roster button — bottom-right, hidden on lg+ where the
          inline sidebar is visible. Sits above every content card via
          fixed positioning. */}
      <button
        onClick={() => setOpen(true)}
        className="lg:hidden fixed bottom-4 right-4 z-40 pill shadow-2xl flex items-center gap-2"
        style={{
          background: "linear-gradient(135deg, rgba(0,229,255,0.18), rgba(11,22,39,0.95))",
          borderColor: "rgba(0,229,255,0.5)",
          color: "#7DD3FC",
          padding: "10px 14px",
          fontSize: 13,
        }}
        data-testid="mobile-roster-fab"
        aria-label="Open athlete roster"
      >
        <Users size={15} />
        <span className="hidden sm:inline">
          {selected ? selected.name : `${activeCount} athletes`}
        </span>
        <span className="sm:hidden font-mono-data text-[11px]">
          {selected
            ? (selected.name.length > 12 ? selected.name.slice(0, 12) + "…" : selected.name)
            : activeCount}
        </span>
        <ChevronUp size={13} className="opacity-70" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[250] lg:hidden"
          data-testid="mobile-roster-sheet"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[#040D1A]/85 backdrop-blur-md"
            onClick={() => setOpen(false)}
          />
          {/* Sheet — slides in from the bottom on phones; from the side
              on tablet-portrait for a nicer landscape feel. */}
          <div
            className="absolute left-0 right-0 bottom-0 bg-[#0B1627] border-t border-[#1E304B] rounded-t-2xl shadow-2xl flex flex-col"
            style={{ maxHeight: "85vh", height: "85vh" }}
          >
            {/* Grab-handle for the "drag me" affordance. Purely visual
                — dismiss is via the X or backdrop tap. */}
            <div className="flex flex-col items-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-600" />
            </div>
            <div className="px-3 pb-2 flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Users size={12} /> Athletes
              </div>
              <button
                onClick={() => setOpen(false)}
                className="pill !p-1.5"
                aria-label="Close roster"
                data-testid="mobile-roster-close-btn"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 min-h-0 px-2 pb-2">
              {/* Reuse the exact desktop sidebar so filters, tabs,
                  and injury/inactivity badges behave identically —
                  auto-close on pick via the wrapped onSelect. */}
              <SailorSidebar
                sailors={sailors}
                selectedId={selectedId}
                onSelect={(id) => { onSelect(id); setOpen(false); }}
                injuriesBySailor={injuriesBySailor}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
